import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { take } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MatMenuTrigger } from '@angular/material/menu';

import states, { IState } from '@core/constants/states';

import { Store } from '@ngrx/store';
import { AppState } from '@state/app.state';
import { setUnsavedStateFiltersAction } from '@state/filters/filters.actions';
import { selectFiltersCurrent } from '@state/filters/filters.selectors';

@UntilDestroy()
@Component({
  selector: 'app-filter-v2-markets',
  templateUrl: './filter-v2-markets.component.html',
  styleUrls: [
    './filter-v2-markets.component.scss',
    '../filters-v2.component.scss',
  ],
})
export class FilterV2MarketsComponent implements OnInit {
  @Input() page = 1;
  @Output() applyFilters = new EventEmitter();
  @ViewChild('trigger') menuTrigger!: MatMenuTrigger;

  protected isOpen = false;
  protected firstChange = true;

  protected checkedCount = 0;
  protected allSelected = false;

  protected markets: IState[] = states;
  protected searchTerm: string = '';
  protected filteredMarkets: IState[] = [];

  public checkedMarkets: { [key: string]: boolean } = states.reduce(
    (acc, state) => ({
      ...acc,
      [state.abbr]: false,
    }),
    {}
  );

  constructor(
    private route: ActivatedRoute,
    private store: Store<AppState>,
    private location: Location
  ) {}

  ngOnInit(): void {
    this.filteredMarkets = [...this.markets];

    // clear filters and URL on state change
    this.store
      .select(selectFiltersCurrent)
      .pipe(untilDestroyed(this))
      .subscribe((filters) => {
        if (!this.firstChange && !filters?.state?.in) {
          this.checkedCount = 0;
          this.allSelected = false;
          this.location.replaceState(`/page/${this.page}`);
          this.checkedMarkets = states.reduce(
            (acc, state) => ({
              ...acc,
              [state.abbr]: false,
            }),
            {}
          );
        }
      });

    // load first from route, then from global state
    this.route.paramMap.pipe(take(1)).subscribe((params) => {
      const states = params.get('states')?.split(',') || [];
      if (states.length) {
        this.loadStatesOnInit(states);
      } else {
        this.store
          .select(selectFiltersCurrent)
          .pipe(take(1))
          .subscribe((filters) => {
            if (filters?.state?.in) {
              this.loadStatesOnInit(filters.state.in);
            }
          });
      }
      this.firstChange = false;
    });
  }

  filterMarkets(): void {
    if (!this.searchTerm.trim()) {
      this.filteredMarkets = [...this.markets];
      return;
    }

    this.filteredMarkets = this.markets.filter((market) =>
      market.name.toLowerCase().includes(this.searchTerm.toLowerCase())
    );
  }

  loadStatesOnInit(values?: string[]) {
    if (values?.length) {
      if (values.length === this.markets.length) this.allSelected = true;
      this.location.replaceState(
        `/page/${this.page}/states/${values.join(',')}`
      );
      this.checkedMarkets = values.reduce(
        (acc, state) => ({
          ...acc,
          [state]: true,
        }),
        {}
      );
      this.onChangeStates();
    }
  }

  onChangeStates(): void {
    const items = Object.keys(this.checkedMarkets).filter(
      (key) => this.checkedMarkets[key]
    );
    this.checkedCount = items.length;
    this.store.dispatch(
      setUnsavedStateFiltersAction({ in: items.length ? items : undefined })
    );
    this.location.replaceState(`/page/${this.page}/states/${items.join(',')}`);
    if (this.checkedCount === this.markets.length) {
      this.allSelected = true;
    } else {
      this.allSelected = false;
    }
    this.applyFilters.emit();
  }

  toggleAllSelection(): void {
    this.allSelected = !this.allSelected;
    this.checkedMarkets = this.markets.reduce(
      (acc, state) => ({
        ...acc,
        [state.abbr]: this.allSelected,
      }),
      {}
    );
    this.onChangeStates();
  }

  resetMarkets(): void {
    this.checkedMarkets = this.markets.reduce(
      (acc, state) => ({
        ...acc,
        [state.abbr]: false,
      }),
      {}
    );
    this.allSelected = false;
    this.onChangeStates();
  }

  closeMenu(): void {
    this.menuTrigger.closeMenu();
  }
}
